'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Admin } from './types';
import { adminApi, SESSION_EXPIRED_EVENT } from './api';
import { AlertCircle, X } from 'lucide-react';

interface AuthContextType {
  admin: Admin | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Session Expired Modal Component
function SessionExpiredModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  if (!isOpen) return null;

  return (
    <div data-component-id="src/lib/auth-context.tsx:25:4" data-component-path="src/lib/auth-context.tsx" data-component-line="25" data-component-end-line="56" data-component-file="auth-context.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20inset-0%20z-50%20flex%20items-center%20justify-center%22%7D" className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div data-component-id="src/lib/auth-context.tsx:27:6" data-component-path="src/lib/auth-context.tsx" data-component-line="27" data-component-end-line="27" data-component-file="auth-context.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-black%2F50%20backdrop-blur-sm%22%7D" className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div data-component-id="src/lib/auth-context.tsx:30:6" data-component-path="src/lib/auth-context.tsx" data-component-line="30" data-component-end-line="55" data-component-file="auth-context.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20bg-white%20rounded-2xl%20shadow-2xl%20max-w-md%20w-full%20mx-4%20p-6%20animate-in%20fade-in%20zoom-in%20duration-200%22%7D" className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 animate-in fade-in zoom-in duration-200">
        <button data-component-id="src/lib/auth-context.tsx:31:8" data-component-path="src/lib/auth-context.tsx" data-component-line="31" data-component-end-line="36" data-component-file="auth-context.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20top-4%20right-4%20text-gray-400%20hover%3Atext-gray-600%20transition-colors%22%7D"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X data-component-id="src/lib/auth-context.tsx:35:10" data-component-path="src/lib/auth-context.tsx" data-component-line="35" data-component-end-line="35" data-component-file="auth-context.tsx" data-component-name="X" data-component-content="%7B%22elementName%22%3A%22X%22%7D" size={20} />
        </button>

        <div data-component-id="src/lib/auth-context.tsx:38:8" data-component-path="src/lib/auth-context.tsx" data-component-line="38" data-component-end-line="54" data-component-file="auth-context.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" className="text-center">
          <div data-component-id="src/lib/auth-context.tsx:39:10" data-component-path="src/lib/auth-context.tsx" data-component-line="39" data-component-end-line="41" data-component-file="auth-context.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-auto%20w-16%20h-16%20bg-red-100%20rounded-full%20flex%20items-center%20justify-center%20mb-4%22%7D" className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertCircle data-component-id="src/lib/auth-context.tsx:40:12" data-component-path="src/lib/auth-context.tsx" data-component-line="40" data-component-end-line="40" data-component-file="auth-context.tsx" data-component-name="AlertCircle" data-component-content="%7B%22elementName%22%3A%22AlertCircle%22%2C%22className%22%3A%22w-8%20h-8%20text-red-500%22%7D" className="w-8 h-8 text-red-500" />
          </div>

          <h2 data-component-id="src/lib/auth-context.tsx:43:10" data-component-path="src/lib/auth-context.tsx" data-component-line="43" data-component-end-line="43" data-component-file="auth-context.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-xl%20font-bold%20text-gray-900%20mb-2%22%2C%22textContent%22%3A%22Session%20Expired%22%7D" className="text-xl font-bold text-gray-900 mb-2">Session Expired</h2>
          <p data-component-id="src/lib/auth-context.tsx:44:10" data-component-path="src/lib/auth-context.tsx" data-component-line="44" data-component-end-line="46" data-component-file="auth-context.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-gray-600%20mb-6%22%2C%22textContent%22%3A%22Your%20session%20has%20expired.%20Please%20login%20again%20to%20continue.%22%7D" className="text-gray-600 mb-6">
            Your session has expired. Please login again to continue.
          </p>

          <button data-component-id="src/lib/auth-context.tsx:48:10" data-component-path="src/lib/auth-context.tsx" data-component-line="48" data-component-end-line="53" data-component-file="auth-context.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20bg-blue-600%20hover%3Abg-blue-700%20text-white%20font-semibold%20py-3%20px-6%20rounded-xl%20transition-colors%22%2C%22textContent%22%3A%22Login%20Again%22%7D"
            onClick={onClose}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
          >
            Login Again
          </button>
        </div>
      </div>
    </div>
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSessionExpired, setShowSessionExpired] = useState(false);

  const logout = useCallback(() => {
    setAdmin(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('admin');
  }, []);

  // Handle session expiration
  const handleSessionExpired = useCallback(() => {
    logout();
    setShowSessionExpired(true);
  }, [logout]);

  const handleCloseSessionExpiredModal = useCallback(() => {
    setShowSessionExpired(false);
    // Redirect to login page
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }, []);

  // Listen for session expired events
  useEffect(() => {
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [handleSessionExpired]);

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedAdmin = localStorage.getItem('admin');

    if (storedToken && storedAdmin) {
      setToken(storedToken);
      try {
        setAdmin(JSON.parse(storedAdmin));
      } catch {
        localStorage.removeItem('admin');
      }
    }
    setIsLoading(false);
  }, []);

  // Verify token on mount
  useEffect(() => {
    if (token && !admin) {
      refreshProfile();
    }
  }, [token]);

  const refreshProfile = useCallback(async () => {
    try {
      const response = await adminApi.getProfile();
      setAdmin(response.data);
      localStorage.setItem('admin', JSON.stringify(response.data));
    } catch {
      // Token is invalid, clear auth state
      logout();
    }
  }, [logout]);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await adminApi.login({ email, password });
      const { admin: adminData, token: authToken } = response.data;

      setAdmin(adminData);
      setToken(authToken);

      localStorage.setItem('token', authToken);
      localStorage.setItem('admin', JSON.stringify(adminData));
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    admin,
    token,
    isLoading,
    isAuthenticated: !!admin && !!token,
    login,
    logout,
    refreshProfile,
  };

  return (
    <AuthContext.Provider data-component-id="src/lib/auth-context.tsx:156:4" data-component-path="src/lib/auth-context.tsx" data-component-line="156" data-component-end-line="159" data-component-file="auth-context.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>
      {children}
      <SessionExpiredModal data-component-id="src/lib/auth-context.tsx:158:6" data-component-path="src/lib/auth-context.tsx" data-component-line="158" data-component-end-line="158" data-component-file="auth-context.tsx" data-component-name="SessionExpiredModal" data-component-content="%7B%22elementName%22%3A%22SessionExpiredModal%22%7D" isOpen={showSessionExpired} onClose={handleCloseSessionExpiredModal} />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
